/**
 * Post-checkout result for a wallet top-up. One screen per server-derived
 * state, each telling the customer (a) whether money moved and (b) the single
 * next step. Copy rules: never "failed" without "no money was taken / any
 * debit is refunded"; never "pay again" while the bank is still confirming.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  ContentCopy,
  ErrorOutline,
  HourglassTop,
  InfoOutlined,
  ReportProblemOutlined,
} from '@mui/icons-material';
import type { TopupStatus } from '@/services/wallet.api';
import { formatInr } from '@/services/wallet.api';
import type { VerifyPhase } from '@/hooks/useWalletTopup';

interface Props {
  verify: VerifyPhase;
  retrying: boolean;
  onClose: () => void;
  onRetry: (status: TopupStatus) => void;
  onCheckAgain: (gatewayOrderId: string, amount?: number) => void;
  onNewTopup: () => void;
}

const REFUND_NOTE =
  'If any amount was debited from your account, it is refunded automatically by your bank or card issuer, usually within 5–7 working days.';

function Reference({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Reference: <b>{id}</b>
      </Typography>
      <Tooltip title={copied ? 'Copied' : 'Copy reference'}>
        <IconButton
          size="small"
          aria-label="Copy payment reference"
          onClick={() => {
            void navigator.clipboard?.writeText(id).then(() => setCopied(true));
          }}
        >
          <ContentCopy sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function Shell({
  icon,
  title,
  children,
  actions,
  reference,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  actions: React.ReactNode;
  reference?: string;
}) {
  return (
    <DialogContent sx={{ textAlign: 'center', p: 4 }}>
      <Box sx={{ mb: 2 }}>{icon}</Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom role="status" aria-live="polite">
        {title}
      </Typography>
      <Box sx={{ color: 'text.secondary', typography: 'body2' }}>{children}</Box>
      <Stack spacing={1} sx={{ mt: 3 }}>
        {actions}
      </Stack>
      {reference && <Reference id={reference} />}
    </DialogContent>
  );
}

const iconSx = { fontSize: 56 };

const TopupResultDialog: React.FC<Props> = ({ verify, retrying, onClose, onRetry, onCheckAgain, onNewTopup }) => {
  if (verify.phase === 'idle') return null;

  // While verifying, closing is allowed (the order is remembered and re-checked
  // on the next visit) — but we say so, to stop a panicked second payment.
  const body = (() => {
    if (verify.phase === 'verifying') {
      const s = verify.last?.state;
      const amount = verify.last?.amount ?? verify.amount;
      return (
        <Shell
          icon={<CircularProgress size={52} />}
          title={s === 'processing' ? 'Your bank is confirming the payment…' : s === 'crediting' ? 'Payment received — adding it to your wallet…' : 'Confirming your payment…'}
          actions={
            <Button color="inherit" onClick={onClose}>
              Close — I'll check later
            </Button>
          }
          reference={verify.gatewayOrderId}
        >
          {amount ? <Typography fontWeight={600}>{formatInr(amount)}</Typography> : null}
          Please don't pay again. This usually takes a few seconds.
        </Shell>
      );
    }

    if (verify.phase === 'unknown') {
      return (
        <Shell
          icon={<InfoOutlined color="info" sx={iconSx} />}
          title="We couldn't confirm the payment yet"
          actions={
            <>
              <Button variant="contained" onClick={() => onCheckAgain(verify.gatewayOrderId, verify.amount)}>
                Check again
              </Button>
              <Button color="inherit" onClick={onClose}>
                Close
              </Button>
            </>
          }
          reference={verify.gatewayOrderId}
        >
          {verify.message}
        </Shell>
      );
    }

    const st = verify.status;
    switch (st.state) {
      case 'credited':
        return (
          <Shell
            icon={<CheckCircle color="success" sx={iconSx} />}
            title={`${formatInr(st.walletCredit || st.amount)} added to your wallet`}
            actions={
              <Button variant="contained" onClick={onClose}>
                Done
              </Button>
            }
            reference={st.gatewayOrderId}
          >
            {typeof st.balance === 'number' && (
              <Typography sx={{ mb: 1 }}>
                New balance: <b>{formatInr(st.balance)}</b>
              </Typography>
            )}
            Payment successful. A receipt has been issued to your workspace.
          </Shell>
        );
      case 'crediting':
        return (
          <Shell
            icon={<CheckCircle color="success" sx={iconSx} />}
            title="Payment received"
            actions={
              <>
                <Button variant="contained" onClick={() => onCheckAgain(st.gatewayOrderId, st.amount)}>
                  Check again
                </Button>
                <Button color="inherit" onClick={onClose}>
                  Close
                </Button>
              </>
            }
            reference={st.gatewayOrderId}
          >
            We've received {formatInr(st.amount)}. It is being added to your wallet and will appear shortly — you don't
            need to pay again. If it doesn't appear within an hour, contact support with the reference below.
          </Shell>
        );
      case 'processing':
        return (
          <Shell
            icon={<HourglassTop color="warning" sx={iconSx} />}
            title="Payment is still processing"
            actions={
              <>
                <Button variant="contained" onClick={() => onCheckAgain(st.gatewayOrderId, st.amount)}>
                  Check again
                </Button>
                <Button color="inherit" onClick={onClose}>
                  Close
                </Button>
              </>
            }
            reference={st.gatewayOrderId}
          >
            Your bank hasn't confirmed this {formatInr(st.amount)} payment yet. <b>Please don't pay again.</b> If it
            succeeds, your wallet is credited automatically. If it fails, any amount debited is refunded by your bank.
          </Shell>
        );
      case 'awaiting':
      case 'cancelled':
        return (
          <Shell
            icon={<InfoOutlined color="info" sx={iconSx} />}
            title={st.state === 'cancelled' ? 'Payment cancelled' : 'Payment not completed'}
            actions={
              <>
                <Button variant="contained" disabled={retrying} onClick={() => onRetry(st)}>
                  {retrying ? 'Opening…' : `Pay ${formatInr(st.amount)}`}
                </Button>
                <Button color="inherit" onClick={onClose}>
                  Not now
                </Button>
              </>
            }
            reference={st.gatewayOrderId}
          >
            No money was taken for this top-up. You can complete the payment whenever you're ready.
          </Shell>
        );
      case 'failed':
        return (
          <Shell
            icon={<ErrorOutline color="error" sx={iconSx} />}
            title="Payment failed"
            actions={
              <>
                <Button variant="contained" disabled={retrying} onClick={() => onRetry(st)}>
                  {retrying ? 'Opening…' : 'Try again'}
                </Button>
                <Button color="inherit" onClick={onClose}>
                  Close
                </Button>
              </>
            }
            reference={st.gatewayOrderId}
          >
            {st.reason && <Typography sx={{ mb: 1 }}>{st.reason}</Typography>}
            Your wallet was not charged. {REFUND_NOTE}
            {st.canRetry ? ' You can retry with the same or another payment method.' : ''}
          </Shell>
        );
      case 'expired':
      default:
        return (
          <Shell
            icon={<ReportProblemOutlined color="warning" sx={iconSx} />}
            title="Payment session expired"
            actions={
              <>
                <Button variant="contained" onClick={onNewTopup}>
                  Start a new top-up
                </Button>
                <Button color="inherit" onClick={onClose}>
                  Close
                </Button>
              </>
            }
            reference={st.gatewayOrderId}
          >
            This payment wasn't completed in time, so no money was taken. {REFUND_NOTE}
          </Shell>
        );
    }
  })();

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      {body}
    </Dialog>
  );
};

export default TopupResultDialog;
